// Modell-Registry-Ansicht (Issue #266).
//
// Ersetzt den früheren Roh-Link `<a href="/api/v1/models">`, der beim Browser-Klick ohne den
// Authorization-Bearer-Header navigierte und deshalb ein 401 (unauthenticated) zeigte. Diese
// Ansicht lädt die Registry über den authentifizierten API-Client (listModels/getModelManifest);
// der Client hängt das Bearer-Token aus dem localStorage an jeden Request. Ein 401 verwirft der
// Client (clearToken) und stößt über das AUTH_REQUIRED_EVENT den LoginGate an — statt roher
// Navigation zur JSON-API.
//
// Aufbau: links die Modelle mit ihren Versionen (Status-Abzeichen, aktiver Pointer), rechts das
// Manifest der gewählten Version (Ports, Metriken, Notebook-Hash, Governance). Wer das Recht
// „deployen" trägt (Rolle deployer/admin), kann eine Version freigeben (approve) bzw. aktiv
// schalten (activate) — mit Bestätigung; die harte Durchsetzung bleibt beim Backend (403/409).

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import {
  activateModel,
  approveModel,
  getMe,
  getModelManifest,
  hasDeployRight,
  listModels,
} from '../api/client';
import type { ModelManifest, ModelPort, ModelSummary, UserInfo } from '../api/types';
import { Button, EmptyState, PanelHeading } from '../components/ui';

// Eine Version, adressiert über ihr Modell — die Auswahl, für die rechts das Manifest lädt.
interface Selected {
  model: string;
  version: string;
}

// Bekannte Governance-Status → übersetztes Label. Unbekannte Status (das Manifest ist frei
// geformt) zeigen wir roh, damit die UI nie einen Schlüsselpfad anzeigt.
const KNOWN_STATUS = ['draft', 'approved', 'active', 'retired', 'deprecated'] as const;
function statusLabel(t: (k: string) => string, status: string): string {
  return (KNOWN_STATUS as readonly string[]).includes(status)
    ? t(`registry.status.${status}`)
    : status;
}

// Status-Abzeichen je Governance-Status. draft neutral, approved grün, active/aktiv gold,
// retired/deprecated gedämpft. Unbekannte Status fallen neutral zurück.
function statusClass(status: string): string {
  switch (status) {
    case 'approved':
      return 'border border-emerald-500/60 bg-emerald-500/10 text-emerald-300';
    case 'retired':
    case 'deprecated':
      return 'border border-bf-border bg-bf-border/30 text-bf-muted';
    default:
      return 'border border-bf-border bg-bf-navy-light text-bf-white';
  }
}

export function RegistryView({ onClose }: { onClose: () => void }): JSX.Element {
  const { t } = useI18n();
  const { reportError, notify } = useStore();

  const [models, setModels] = useState<ModelSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<UserInfo | null>(null);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [manifest, setManifest] = useState<ModelManifest | null>(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const canGovern = hasDeployRight(me?.roles);

  // Die Registry über den authentifizierten Client laden. Ein 401 stößt (im Client) den
  // LoginGate an; hier melden wir zusätzlich einen sprechenden Fehler in die Toasts.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listModels();
      setModels(res.models);
    } catch (err) {
      setModels([]);
      reportError(err, t('registry.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [reportError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Wer angemeldet ist (Rollen) bestimmt, ob die Governance-Aktionen sichtbar sind. Still, falls
  // /me nicht lesbar ist (z. B. statisches Token) — dann werden die Aktionen einfach nicht gezeigt.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((info) => !cancelled && setMe(info))
      .catch(() => {
        /* keine Rolle ermittelbar → Governance-Aktionen ausgeblendet */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Manifest der gewählten Version laden (Ports/Metriken/Governance).
  const loadManifest = useCallback(
    async (sel: Selected) => {
      setManifestLoading(true);
      setManifest(null);
      try {
        setManifest(await getModelManifest(sel.model, sel.version));
      } catch (err) {
        reportError(err, t('registry.manifestFailed'));
      } finally {
        setManifestLoading(false);
      }
    },
    [reportError, t],
  );

  const choose = useCallback(
    (model: string, version: string) => {
      const sel = { model, version };
      setSelected(sel);
      void loadManifest(sel);
    },
    [loadManifest],
  );

  // Freigabe (approve) einer Version — mit Bestätigung. Danach Liste + Manifest neu laden.
  const doApprove = useCallback(
    async (model: string, version: string) => {
      if (!window.confirm(t('registry.approveConfirm', { model, version }))) return;
      setBusy(true);
      try {
        await approveModel(model, version);
        notify('info', t('registry.approved', { model, version }));
        await load();
        await loadManifest({ model, version });
        setSelected({ model, version });
      } catch (err) {
        reportError(err, t('registry.approveFailed'));
      } finally {
        setBusy(false);
      }
    },
    [t, notify, load, loadManifest, reportError],
  );

  // Aktiv schalten (activate) des current-Pointers — mit Bestätigung.
  const doActivate = useCallback(
    async (model: string, version: string) => {
      if (!window.confirm(t('registry.activateConfirm', { model, version }))) return;
      setBusy(true);
      try {
        await activateModel(model, version);
        notify('info', t('registry.activated', { model, version }));
        await load();
        await loadManifest({ model, version });
        setSelected({ model, version });
      } catch (err) {
        reportError(err, t('registry.activateFailed'));
      } finally {
        setBusy(false);
      }
    },
    [t, notify, load, loadManifest, reportError],
  );

  const empty = models != null && models.length === 0 && !loading;

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col bg-bf-navy"
      data-testid="registry-view"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between border-b border-bf-border px-4 py-3">
        <div>
          <PanelHeading>{t('registry.title')}</PanelHeading>
          <p className="max-w-3xl text-xs text-bf-muted">{t('registry.hint')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => void load()} disabled={loading || busy} testId="registry-reload">
            {t('registry.reload')}
          </Button>
          <Button onClick={onClose} testId="registry-close">
            {t('registry.close')}
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Links: Modelle + Versionen. */}
        <div className="min-h-0 overflow-auto border-b border-bf-border p-4 md:border-b-0 md:border-r">
          {loading && <EmptyState title={t('registry.loading')} />}

          {empty && <EmptyState title={t('registry.empty')} hint={t('registry.emptyHint')} />}

          {!loading && models != null && models.length > 0 && (
            <ul className="flex flex-col gap-4" data-testid="registry-models">
              {models.map((m) => (
                <li key={m.model_id} data-testid={`registry-model-${m.model_id}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-bf-white">{m.model_id}</span>
                    <span className="text-xs text-bf-muted">
                      {m.current
                        ? t('registry.currentIs', { version: m.current })
                        : t('registry.noCurrent')}
                    </span>
                  </div>
                  <ul className="flex flex-col gap-1">
                    {m.versions.map((v) => {
                      const isSel =
                        selected?.model === m.model_id && selected?.version === v.version;
                      const isCurrent = m.current === v.version;
                      return (
                        <li key={v.version}>
                          <button
                            type="button"
                            data-testid={`registry-version-${m.model_id}-${v.version}`}
                            onClick={() => choose(m.model_id, v.version)}
                            className={
                              'flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-sm transition-colors ' +
                              (isSel
                                ? 'border-bf-gold bg-bf-navy-light text-bf-white'
                                : 'border-bf-border text-bf-white hover:border-bf-gold')
                            }
                          >
                            <span className="flex items-center gap-2">
                              <span className="font-medium">{v.version}</span>
                              {isCurrent && (
                                <span className="rounded bg-bf-gold/20 px-1.5 py-0.5 text-xs font-semibold text-bf-gold">
                                  {t('registry.active')}
                                </span>
                              )}
                            </span>
                            <span
                              className={'rounded px-1.5 py-0.5 text-xs ' + statusClass(v.status)}
                              data-testid={`registry-status-${m.model_id}-${v.version}`}
                            >
                              {statusLabel(t, v.status)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Rechts: Manifest der gewählten Version. */}
        <div className="min-h-0 overflow-auto p-4" data-testid="registry-detail">
          {!selected && !manifestLoading && (
            <EmptyState title={t('registry.pickVersion')} />
          )}
          {manifestLoading && <EmptyState title={t('registry.manifestLoading')} />}
          {selected && manifest && !manifestLoading && (
            <ManifestDetail
              manifest={manifest}
              current={
                models?.find((m) => m.model_id === selected.model)?.current === selected.version
              }
              canGovern={canGovern}
              busy={busy}
              onApprove={() => void doApprove(selected.model, selected.version)}
              onActivate={() => void doActivate(selected.model, selected.version)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Das Manifest-Detail: Kopf (Modell/Version/Status), Ports, Metriken, Notebook-Hash, Governance —
// und, wenn die Rolle es trägt, die Aktionen Freigeben/Aktivieren.
function ManifestDetail({
  manifest,
  current,
  canGovern,
  busy,
  onApprove,
  onActivate,
}: {
  manifest: ModelManifest;
  current: boolean;
  canGovern: boolean;
  busy: boolean;
  onApprove: () => void;
  onActivate: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const status = manifest.governance?.status ?? 'draft';
  const metrics = manifest.metrics ?? {};
  const metricKeys = Object.keys(metrics);

  return (
    <div className="flex flex-col gap-4" data-testid="registry-manifest">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-bf-white">
            {manifest.model_id}{' '}
            <span className="text-bf-muted">· {manifest.version}</span>
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span
              className={'rounded px-1.5 py-0.5 text-xs ' + statusClass(status)}
              data-testid="registry-manifest-status"
            >
              {statusLabel(t, status)}
            </span>
            {current && (
              <span className="rounded bg-bf-gold/20 px-1.5 py-0.5 text-xs font-semibold text-bf-gold">
                {t('registry.active')}
              </span>
            )}
            {manifest.format && (
              <span className="text-xs text-bf-muted">{manifest.format}</span>
            )}
          </div>
        </div>
        {canGovern && (
          <div className="flex shrink-0 items-center gap-2">
            {status !== 'approved' && (
              <Button
                variant="primary"
                onClick={onApprove}
                disabled={busy}
                testId="registry-approve"
              >
                {t('registry.approve')}
              </Button>
            )}
            {status === 'approved' && !current && (
              <Button onClick={onActivate} disabled={busy} testId="registry-activate">
                {t('registry.activate')}
              </Button>
            )}
          </div>
        )}
      </div>

      <PortsTable label={t('registry.inputs')} ports={manifest.inputs ?? []} testId="registry-inputs" />
      <PortsTable
        label={t('registry.outputs')}
        ports={manifest.outputs ?? []}
        testId="registry-outputs"
      />

      {metricKeys.length > 0 && (
        <section data-testid="registry-metrics">
          <PanelHeading>{t('registry.metrics')}</PanelHeading>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
            {metricKeys.map((k) => (
              <div key={k} className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-bf-muted">{k}</dt>
                <dd className="text-bf-white">{String(metrics[k])}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {manifest.source_notebook_hash && (
        <section>
          <PanelHeading>{t('registry.notebookHash')}</PanelHeading>
          <p className="break-all font-mono text-xs text-bf-muted">
            {manifest.source_notebook_hash}
          </p>
        </section>
      )}

      {manifest.governance && (
        <section data-testid="registry-governance">
          <PanelHeading>{t('registry.governance')}</PanelHeading>
          <dl className="flex flex-col gap-1 text-sm text-bf-white">
            {manifest.governance.approved_by && (
              <div className="flex gap-2">
                <dt className="text-bf-muted">{t('registry.approvedBy')}:</dt>
                <dd>{manifest.governance.approved_by}</dd>
              </div>
            )}
            {manifest.governance.approved_at && (
              <div className="flex gap-2">
                <dt className="text-bf-muted">{t('registry.approvedAt')}:</dt>
                <dd>{manifest.governance.approved_at}</dd>
              </div>
            )}
            {manifest.governance.note && (
              <div className="flex gap-2">
                <dt className="text-bf-muted">{t('registry.note')}:</dt>
                <dd>{manifest.governance.note}</dd>
              </div>
            )}
          </dl>
        </section>
      )}
    </div>
  );
}

// Eine kleine Port-Tabelle (Name/Größe/Einheit/Typ). Leere Listen zeigen einen dezenten Hinweis.
function PortsTable({
  label,
  ports,
  testId,
}: {
  label: string;
  ports: ModelPort[];
  testId: string;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <section data-testid={testId}>
      <PanelHeading>{label}</PanelHeading>
      {ports.length === 0 ? (
        <p className="text-xs text-bf-muted">{t('registry.noPorts')}</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-bf-muted">
              <th className="py-1 pr-3 font-medium">{t('registry.portName')}</th>
              <th className="py-1 pr-3 font-medium">{t('registry.portQuantity')}</th>
              <th className="py-1 pr-3 font-medium">{t('registry.portUnit')}</th>
              <th className="py-1 font-medium">{t('registry.portDtype')}</th>
            </tr>
          </thead>
          <tbody>
            {ports.map((p, i) => (
              <tr key={`${p.name ?? i}`} className="border-t border-bf-border/60 text-bf-white">
                <td className="py-1 pr-3 font-medium">{p.name ?? '—'}</td>
                <td className="py-1 pr-3 text-bf-muted">{p.quantity ?? '—'}</td>
                <td className="py-1 pr-3 text-bf-muted">{p.unit ?? '—'}</td>
                <td className="py-1 text-bf-muted">{p.dtype ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
